import type { trackEcommerce } from '@svenskaspelab/nova-core/nextjs/analytics';

import type { DataTransferObject } from '../@types/DataTransferObject.js';
import type { IPayable } from '../@types/Payable.js';
import type { IService, PaidPayableItem } from '../@types/Service.js';
import { BaseError } from '../error/BaseError.js';
import { CollectionError } from '../error/CollectionError.js';
import type { PayableGenerator } from '../payment/context/createPaymentContextApi.js';
import { logger } from '../utils/index.js';

export type PaymentConfig = {
  haltPaymentOnException: boolean;
  trackTransaction?: typeof trackEcommerce;
};

/**
 * Base class for all payment services
 * Used to pass a payable item through the payment process (createPayment)
 */
export abstract class PaymentService<
  TPayable extends IPayable,
  TConfig extends PaymentConfig = PaymentConfig,
> implements IService<TPayable> {
  private readonly _config: TConfig;

  /**
   * Creates a new payment service
   *
   * Configuration object:
   * - haltPaymentOnException - boolean, if true the payment process will halt on the first exception. Default is true.
   *
   * @param nextConfig - Configuration object
   */
  constructor(nextConfig: TConfig = {} as TConfig) {
    // config object should state every default configuration that could be done
    this._config = Object.assign(
      {
        haltPaymentOnException: true,
      },
      nextConfig
    );
  }

  /**
   * Returns the payment service configuration.
   *
   * @returns The payment service configuration
   */
  get config(): TConfig {
    return this._config;
  }

  /**
   * Processes a collection of payable items and calls underlying processRequest (implemented by subclass) for each payable.
   * Can operate in two ways via config.haltPaymentOnException, true: pays every payable until the first fails
   * and the payment process will exit OR
   *   false: it will try to pay for every wager within the collection.
   *
   * @param payableItems - An array of payable items
   * @returns An array containing the payment responses for each payable item as first item as array and Error instances if any error occurred as second item as array
   */
  async initiatePaymentRequest(
    payableItems: PayableGenerator<TPayable>
  ): Promise<readonly [PaidPayableItem<TPayable>[], CollectionError | undefined]> {
    const paymentResponses: PaidPayableItem<TPayable>[] = [];
    const failedPayableItems: {
      payable: IPayable;
      error: Error;
    }[] = [];
    let count = 0;
    const collectionError = new CollectionError();

    for await (const payable of payableItems) {
      try {
        count++;
        const payableResponse = await this.processRequest(payable);
        paymentResponses.push({ payable, dto: payableResponse });

        logger.info('Payment successful', {
          current: count,
          total: payableItems.length,
          transactionId: payableResponse.transactionId,
          payable: payable.toString(),
        });

        // Use custom event to trigger notify other subscribed components
        // publishEvent(eventNames.PAYMENT_SUCCESS, { detail: { payable, dto: payableResponse } });
      } catch (error) {
        if (error instanceof Error) {
          const errorMessage = error instanceof BaseError ? error.toString() : error.message;
          logger.warn('Payment failed', {
            current: count,
            total: payableItems.length,
            error: errorMessage,
            payable: payable.toString(),
          });

          failedPayableItems.push({ payable, error });
          // TODO: är det intressant att ha ett 1:1 förhållande mellan payable och error?
          // Detta kan göras genom att CollectionError.addError tar emot en optional LocalId som andra argument
          collectionError.addError(error);
        }

        if (this.config.haltPaymentOnException) {
          break;
        }
      }
    }

    // Log remaining payable items in case of exception
    const failedPayables = failedPayableItems.map(({ payable }) => payable);
    const remainingPayableItems = [...failedPayables, ...payableItems.remaining];
    let failCount = payableItems.length - remainingPayableItems.length;
    for (const unpaidPayable of remainingPayableItems) {
      failCount++;
      logger.info('Payment failed for item', {
        failedCount: failCount,
        total: payableItems.length,
        payable: unpaidPayable.toString(),
      });
      count++;
    }

    return [paymentResponses, failedPayableItems.length > 0 ? collectionError : undefined] as const;
  }

  /**
   * Processes a single payable item
   * Implemented by subclass
   *
   * @param payable - The payable item
   * @returns - The payment DTO response
   */
  abstract processRequest(payable: IPayable): Promise<DataTransferObject>;

  /**
   * Called when the payment process is done
   * Implemented by subclass
   *
   * @param paidPayableItems - An array of paid payable items,
   * each object contains the payable (key: payable) and the payment DTO response (key: dto)
   * @returns
   */
  abstract onPaymentDone(paidPayableItems: readonly PaidPayableItem<TPayable>[]): void;

  /**
   * Called on the next tick (setTimeout) when the payment process is done.
   * Should be used to track the payment process.
   *
   * Implemented by subclass
   *
   * @param paidPayableItems - An array of paid payable items,
   * each object contains the payable (key: payable) and the payment DTO response (key: dto)
   * @returns
   */
  abstract trackPayments(paidPayableItems: readonly PaidPayableItem<TPayable>[]): void;
}
